import { useCallback, useContext } from "react";
import { ArticleContent, FormattedContent } from "../@types";
import { useContentAndProgress } from "../contexts/UserProgressContext";
import { useLocation, useNavigate } from "react-router-dom";
import { makeUrlFromTitle } from "../utils";
import {
  getContentById,
  getUserContentAndProgressTree,
} from "../services/content.service";
import { useContentActions } from "./useContentActions";
import { AuthContext } from "../contexts/AuthContext";

export function useContentNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeMilestone, openedContent, activePhase } =
    useContentAndProgress();
  const { user } = useContext(AuthContext);
  const goToNextContent = useCallback(async () => {
    if (
      !activePhase ||
      !activeMilestone ||
      !activeMilestone.content ||
      !activeMilestone.content.childContentIds ||
      !openedContent
    ) {
      return { next: false };
    }

    const contents = activeMilestone.content.childContentIds;
    const currentContentPlatformId = openedContent;

    const currentIndex = contents.findIndex(
      (contentId: string) => contentId === currentContentPlatformId
    );

    if (currentIndex === -1) {
      console.error(
        "Current content (openedContent) not found in active milestone's children."
      );
      return { next: false };
    }

    let nextIndex = currentIndex + 1;
    while (nextIndex < contents.length) {
      const nextContentPlatformId = contents[nextIndex];
      try {
        // const nextContentData = await getContentById({
        //   platformContentId: nextContentPlatformId,
        // });

        const contentWithProgress = await getUserContentAndProgressTree({
          platformContentId: nextContentPlatformId,
        });

        if (
          !(contentWithProgress as any).content.hideFromMilestone &&
          contentWithProgress &&
          !(contentWithProgress as any)?.markedIrrelevant
        ) {
          const basePath = `/phase/${activePhase.content.platformContentId}/milestone/${activeMilestone.content.platformContentId}/content/`;
          const content = (contentWithProgress as any).content;
          const localizedTitle =
            user?.locale === "de"
              ? content.contentTitleDe || content.contentTitleEn
              : content.contentTitleEn || content.contentTitleDe;
          navigate(
            `${basePath}${
              (contentWithProgress as any).content.platformContentId
            }/${makeUrlFromTitle(localizedTitle)}`
          );

          return;
        }
      } catch (error) {
        console.error(
          `Error fetching content data for ${nextContentPlatformId}:`,
          error
        );
        // Continue to the next content item if this one fails
      }
      nextIndex++;
    }

    return { next: false };
  }, [activeMilestone, openedContent, activePhase, navigate]);

  const goToPreviousContent = useCallback(async () => {
    if (
      !activePhase ||
      !activeMilestone ||
      !activeMilestone.content ||
      !activeMilestone.content.childContentIds ||
      !openedContent
    ) {
      return;
    }

    const contents = activeMilestone.content.childContentIds;
    const currentContentPlatformId = openedContent;

    const currentIndex = contents.findIndex(
      (contentId: string) => contentId === currentContentPlatformId
    );

    if (currentIndex === -1) {
      console.error(
        "Current content (openedContent) not found for previous navigation."
      );
      return;
    }

    let prevIndex = currentIndex - 1;
    while (prevIndex >= 0) {
      const prevContentPlatformId = contents[prevIndex];
      try {
        // const prevContentData = await getContentById({
        //   platformContentId: prevContentPlatformId,
        // });

        const contentWithProgress = await getUserContentAndProgressTree({
          platformContentId: prevContentPlatformId,
        });
        if (
          contentWithProgress &&
          !(contentWithProgress as any).content.hideFromMilestone &&
          !(contentWithProgress as any).markedIrrelevant
        ) {
          const basePath = `/phase/${activePhase.content.platformContentId}/milestone/${activeMilestone.content.platformContentId}/content/`;

          const content = (contentWithProgress as any).content;
          const localizedTitle =
            user?.locale === "de"
              ? content.contentTitleDe || content.contentTitleEn
              : content.contentTitleEn || content.contentTitleDe;

          navigate(
            `${basePath}${
              (contentWithProgress as any).content.platformContentId
            }/${makeUrlFromTitle(localizedTitle)}`
          );
          return;
        }
      } catch (error) {
        console.error(
          `Error fetching content data for ${prevContentPlatformId}:`,
          error
        );
      }
      prevIndex--;
    }

    return;
  }, [
    activeMilestone,
    openedContent,
    activePhase,
    navigate,
    location.pathname,
  ]);

  return {
    goToNextContent,
    goToPreviousContent,
  };
}
